import { supabase } from '@/lib/supabase/client';
import { z } from 'zod';
import type { Tables } from '@/lib/supabase/types';
import type { Selector } from '@/lib/annotations/selectors';

export type AnnotationRow = Tables<'annotations'>;

export type AnnotationVisibility = 'public' | 'private' | 'author_only';

/** Display-layer type used by UI components — computed from the DB row + profile join. */
export interface DisplayAnnotation {
  id: string;
  selector: Selector;
  excerpt: string;
  bodyMd: string;
  authorHandle: string;
  authorName: string;
  initials: string;
  color: string;
  isPublic: boolean;
  createdAt: string;
}

const PALETTE = ['#2563eb', '#7c3aed', '#059669', '#dc2626', '#d97706', '#0891b2'];

function hashColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) & 0xffffffff;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

function makeInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .map(p => p[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export function toDisplayAnnotation(
  row: AnnotationRow,
  profile: { handle: string; display_name: string | null },
): DisplayAnnotation {
  const selector = row.selector as unknown as Selector;
  const name = profile.display_name ?? profile.handle;
  return {
    id: row.id,
    selector,
    excerpt: selector.quote.exact.slice(0, 200),
    bodyMd: row.body_md,
    authorHandle: profile.handle,
    authorName: name,
    initials: makeInitials(name),
    color: hashColor(row.author_id),
    isPublic: row.visibility === 'public',
    createdAt: row.created_at,
  };
}

// ── Zod schemas ───────────────────────────────────────────────────────────────

const selectorSchema = z.object({
  position: z.object({ start: z.number(), end: z.number() }),
  quote: z.object({ exact: z.string(), prefix: z.string(), suffix: z.string() }),
});

export const createAnnotationSchema = z.object({
  versionId: z.string().uuid('versionId must be a UUID'),
  selector: selectorSchema,
  bodyMd: z.string().min(1, 'Annotation body is required').max(2000),
  visibility: z.enum(['public', 'private', 'author_only']),
});

// ── API functions ─────────────────────────────────────────────────────────────

export async function createAnnotation(
  versionId: string,
  selector: Selector,
  bodyMd: string,
  visibility: AnnotationVisibility,
): Promise<AnnotationRow> {
  createAnnotationSchema.parse({ versionId, selector, bodyMd, visibility });

  const { data, error } = await supabase.rpc('create_annotation', {
    p_version_id: versionId,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Selector satisfies Json; cast needed due to generated type being Json
    p_selector: selector as any,
    p_body_md: bodyMd,
    p_visibility: visibility,
  });
  if (error) throw error;
  return data as unknown as AnnotationRow;
}

type AnnotationWithProfile = AnnotationRow & {
  profiles: { handle: string; display_name: string | null } | null;
};

export async function getAnnotationsByVersion(
  versionId: string,
): Promise<{ rows: AnnotationWithProfile[]; display: DisplayAnnotation[] }> {
  const { data, error } = await supabase
    .from('annotations')
    .select('*, profiles!author_id(handle, display_name)')
    .eq('document_version_id', versionId)
    .is('deleted_at', null)
    .is('hidden_at', null)
    .order('created_at', { ascending: true });

  if (error) throw error;
  const rows = (data ?? []) as unknown as AnnotationWithProfile[];
  const display = rows
    .filter(r => r.profiles)
    .map(r => toDisplayAnnotation(r, r.profiles!));
  return { rows, display };
}
